import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Ref } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { getActiveSectionKey, getSectionScrollRequest, setActiveSection } from '@makemydeal/dr-sales-checkin-store';
import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import type { ShowroomSectionKey } from '@makemydeal/dr-sales-checkin-types';

import {
    LAZY_MOUNT_THRESHOLDS,
    LAZY_MOUNT_VISIBLE_RATIO,
    SCROLLING_KEYS,
    SETTLE_IDLE_MS,
    SETTLE_MAX_MS,
    USER_INTENT_EVENTS,
    createScrollContainer,
    resolveActiveSection,
    scrollToNode,
    sectionHeightCache,
    sectionKeyOf,
    visibleFractionOf
} from '@makemydeal/dr-sales-checkin-ui-utils';

/**
 * The scroll engine behind the continuous-scroll showroom page. It owns four jobs, one effect each:
 *
 * 1. **Arrival** (layout effect): before the first paint, mount and jump to the section the page
 *    should open at.
 * 2. **Commands** (effect on `sectionScrollRequest`): service "go to this section" asks from the store,
 *    mounting the target before the scroll starts.
 * 3. **Lazy mount** (effect on the section list): mount a section's content once enough of it is on
 *    screen. Sections passed while a command settles wait for it to settle.
 * 4. **Spy** (effect on the section list): observe where the user has scrolled to and report it back,
 *    and hold the reading position when a section above it changes height.
 *
 * The split between 2 and 4 is the command/observation split: `sectionScrollRequest` is "go here, for
 * this reason", `activeSectionKey` is "the user is here". The highlight math itself is pure and lives
 * in `resolveActiveSection`; discovering *which* element scrolls lives in `createScrollContainer`.
 *
 * The mounted set is local to the page and only grows (keep-alive), so a section's content stays
 * mounted until the page itself unmounts.
 */
export const useScrollEngine = ({ sectionKeys }: { sectionKeys: ReadonlyArray<ShowroomSectionKey> }) => {
    const stackRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    // `useStore` reads state without subscribing: the spy needs the current highlight to avoid
    // re-dispatching it, but must not re-render the page every time the highlight moves.
    const store = useStore<SalesCheckinRootState>();
    const request = useSelector(getSectionScrollRequest);
    const rememberedKey = useSelector(getActiveSectionKey);

    const nodesRef = useRef(new Map<ShowroomSectionKey, HTMLElement>());
    const refsRef = useRef(new Map<ShowroomSectionKey, Ref<HTMLElement>>());
    /** True while a requested scroll is in flight; the spy stays silent so the highlight doesn't strobe. */
    const isSettlingRef = useRef(false);
    /**
     * The last request serviced. Seeded from the request already in the store at mount, so a leftover
     * request (e.g. the one from before the user opened a takeover) is treated as already done —
     * positioning at mount belongs to the arrival effect, not here.
     */
    const consumedIdRef = useRef(request?.id ?? 0);
    /** Tears down the in-flight settle watch — used when a newer request arrives, and on unmount. */
    const endSettlingRef = useRef<() => void>();
    const hasArrivedRef = useRef(false);
    const signature = sectionKeys.join('|');

    const [mountedKeys, setMountedKeys] = useState<ReadonlySet<ShowroomSectionKey>>(() => new Set());
    const mountedKeysRef = useRef(mountedKeys);
    mountedKeysRef.current = mountedKeys;
    const lastHeightsRef = useRef(new Map<ShowroomSectionKey, number>());
    /** The section held in place until the user acts — set by Arrival and by every command jump. */
    const arrivalKeyRef = useRef<ShowroomSectionKey>();
    /** Detaches the current command hold's user-intent listeners — a newer request, or unmount. */
    const detachCommandHoldRef = useRef<() => void>();

    const mount = useCallback((keys: Iterable<ShowroomSectionKey>) => {
        const requested = [...keys];

        if (requested.every((key) => mountedKeysRef.current.has(key))) {
            return;
        }

        setMountedKeys((current) => {
            const additions = requested.filter((key) => !current.has(key));

            return additions.length ? new Set([...current, ...additions]) : current;
        });
    }, []);

    /**
     * The one mount rule for a section the user was not sent to: at least `LAZY_MOUNT_VISIBLE_RATIO`
     * of it is on screen, measured from live layout against the real viewport.
     *
     * Measuring beats subscribing here. Every other signal the engine has is a proxy — the observer's
     * thresholds are fractions of the anchor, not of the view, and a margin is a guess about where
     * the user is about to look — and each proxy was wrong in a way the user could see: a margin
     * mounted Trade-In while it was still off screen, and suppressing mounts under an arrival hold
     * left Customer Details a skeleton in plain sight above a Deal Summary the page had no room to
     * scroll to the top. So the proxies only decide *when to re-measure*; this decides what mounts.
     *
     * Safe to run under a hold: `holdReadingPosition` re-snaps to the held target on every height
     * change, which is what the hold is for.
     */
    const mountVisibleSections = useCallback(() => {
        const { top, height } = createScrollContainer(stackRef.current).getViewport();
        const visible: ShowroomSectionKey[] = [];

        nodesRef.current.forEach((node, key) => {
            if (visibleFractionOf(node.getBoundingClientRect(), top, height) >= LAZY_MOUNT_VISIBLE_RATIO) {
                visible.push(key);
            }
        });

        if (visible.length) {
            mount(visible);
        }
    }, [mount]);

    const holdReadingPosition = useCallback(
        (measurements: ReadonlyArray<readonly [ShowroomSectionKey, number]>) => {
            const activeKey = getActiveSectionKey(store.getState());
            const activeIndex = activeKey ? sectionKeys.indexOf(activeKey) : -1;
            let delta = 0;
            let hasChanged = false;

            measurements.forEach(([key, height]) => {
                const previous = lastHeightsRef.current.get(key);
                lastHeightsRef.current.set(key, height);

                if (mountedKeysRef.current.has(key)) {
                    sectionHeightCache.set(key, height);
                }

                if (previous === undefined || previous === height) {
                    return;
                }

                hasChanged = true;

                if (sectionKeys.indexOf(key) < activeIndex) {
                    delta += height - previous;
                }
            });

            if (!hasChanged || isSettlingRef.current) {
                return;
            }

            const container = createScrollContainer(stackRef.current);
            const arrivalNode = arrivalKeyRef.current ? nodesRef.current.get(arrivalKeyRef.current) : undefined;

            if (arrivalNode) {
                scrollToNode(container, arrivalNode, 'auto');
                return;
            }

            if (delta) {
                container.scrollToOffset(container.getMetrics().scrollTop + delta, 'auto');
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, signature]
    );

    /**
     * One ref callback per section key, created once and reused forever. The stable identity is what
     * lets `SectionSlot` stay memoized: a fresh `ref={node => …}` on every render would be a changed
     * prop, and every highlight change would re-render all four activities.
     */
    const getSectionRef = useCallback((key: ShowroomSectionKey): Ref<HTMLElement> => {
        const existing = refsRef.current.get(key);
        if (existing) {
            return existing;
        }

        const sectionRef = (node: HTMLElement | null) => {
            if (node) {
                nodesRef.current.set(key, node);
            } else {
                nodesRef.current.delete(key);
            }
        };

        refsRef.current.set(key, sectionRef);

        return sectionRef;
    }, []);

    /**
     * ARRIVAL — where the page opens.
     *
     * Runs in a layout effect so the jump lands before the first paint; the user never sees the page
     * at the top and then move. The target is the remembered section only (set when they last left
     * the page, which is what gives return-from-takeover, or by `navigationMiddleware` before the
     * page mounts for a workflow or navigation arrival), falling back to the first section when that
     * key is not among this page's. With no remembered key at all — the status is still `IDLE` and
     * the middleware has not yet activated anything — this effect does nothing and leaves positioning
     * to the Commands effect once a `sectionScrollRequest` arrives.
     *
     * The target's content is mounted here, and the state update flushes before the paint, so the
     * section the page opens at is the one already rendered.
     *
     * Until the user first scrolls, taps, clicks or types, or a scroll request arrives, the target is
     * held: any section changing height re-aligns it, however long the content takes to load. A
     * height delta alone is not enough here. The target often renders short at first, the browser then
     * clamps the scroll position because the page is not tall enough to keep it at the top, and the
     * spy reports a section above as active, so holding by delta would hold the wrong section.
     */
    useLayoutEffect(() => {
        if (hasArrivedRef.current || !sectionKeys.length) {
            return undefined;
        }
        hasArrivedRef.current = true;

        const targetKey = rememberedKey ? (sectionKeys.includes(rememberedKey) ? rememberedKey : sectionKeys[0]) : undefined;

        if (!targetKey) {
            return undefined;
        }

        const node = nodesRef.current.get(targetKey);

        if (!node) {
            return undefined;
        }

        mount([targetKey]);
        scrollToNode(createScrollContainer(stackRef.current), node, 'auto');
        // The spy only runs on events, so without this the sidebar would highlight nothing until the
        // user's first scroll.
        dispatch(setActiveSection(targetKey));

        arrivalKeyRef.current = targetKey;
        const detach = () => USER_INTENT_EVENTS.forEach((type) => window.removeEventListener(type, release));
        const release = () => {
            arrivalKeyRef.current = undefined;
            detach();
            mountVisibleSections();
        };
        USER_INTENT_EVENTS.forEach((type) => window.addEventListener(type, release, { passive: true }));

        return () => {
            arrivalKeyRef.current = undefined;
            detach();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    /**
     * MOUNT COMPENSATION — holding the reading position when content mounts.
     *
     * Every change to the mounted set is measured here, before the paint, against the heights from
     * the commit before it. Sections near the view mount almost together on arrival, often before the
     * first `ResizeObserver` report; measuring only there would take the grown heights as the
     * baseline and let the sections above push the target down.
     */
    useLayoutEffect(() => {
        const measurements: Array<readonly [ShowroomSectionKey, number]> = [];

        nodesRef.current.forEach((node, key) => {
            const { height } = node.getBoundingClientRect();

            if (typeof height === 'number') {
                measurements.push([key, height]);
            }
        });

        holdReadingPosition(measurements);
        mountVisibleSections();
    }, [mountedKeys, holdReadingPosition, mountVisibleSections]);

    /** A scroll still settling when the page unmounts must release its listeners and timers. */
    useEffect(
        () => () => {
            endSettlingRef.current?.();
            detachCommandHoldRef.current?.();
        },
        []
    );

    /**
     * COMMANDS — servicing "take me to this section".
     *
     * Consumes a `sectionScrollRequest` once, identified by its `id`: the id advances on every
     * activation, including a repeat of the same section, which is what lets a second click on the
     * same sidebar card scroll again.
     *
     * The jump itself is always instant, and settling never moves focus — that was dropped along
     * with `SectionScrollOrigin` (no production code could tell a deliberate card click apart from
     * a background workflow move without it, and stealing focus from an open input on the wrong
     * one was worse than moving focus on neither).
     *
     * While the scroll is in flight we "settle": the spy is held back so the highlight doesn't strobe
     * through the sections being passed. Settling ends on `scrollend`, after a quiet spell for the
     * browsers that don't fire it, or at a hard cap. Any sign of the user scrolling themselves
     * cancels it immediately — they win.
     *
     * Settling alone cannot keep the target in place: the jump is instant, and Chrome fires
     * `scrollend` synchronously inside it, so settling is over before React has even committed the
     * target's mount. The target and the sections near the old position then swap skeletons for
     * content, and a GTL refresh re-renders content, all with the spy live — the layout moves under
     * the viewport and the spy reports whichever section is now on screen, overwriting the one this
     * request activated. So, like Arrival, the target is held until the user scrolls, taps, clicks
     * or types: `holdReadingPosition` re-snaps to it on every height change meanwhile, so the spy
     * keeps reading the target. A layout effect for the same reason as Arrival: the jump and the
     * hold land before the paint.
     */
    useLayoutEffect(() => {
        if (!request || request.id === consumedIdRef.current) {
            return;
        }
        consumedIdRef.current = request.id;
        arrivalKeyRef.current = undefined;

        const nodes = nodesRef.current;
        // An unavailable target (a section the dealer's edition hides) falls back to the first one.
        const targetKey = nodes.has(request.sectionKey) ? request.sectionKey : sectionKeys[0];
        const node = targetKey ? nodes.get(targetKey) : undefined;

        if (!targetKey || !node) {
            return;
        }

        const container = createScrollContainer(stackRef.current);

        // A newer request supersedes whatever was still settling.
        endSettlingRef.current?.();

        const timers: { idle?: ReturnType<typeof setTimeout>; cap?: ReturnType<typeof setTimeout> } = {};

        /** Releases the settle watch and hands observation back to the spy. */
        const stop = () => {
            clearTimeout(timers.idle);
            clearTimeout(timers.cap);
            container.eventTarget.removeEventListener('scroll', onScroll);
            container.eventTarget.removeEventListener('scrollend', settle);
            container.eventTarget.removeEventListener('wheel', cancel);
            container.eventTarget.removeEventListener('touchstart', cancel);
            container.eventTarget.removeEventListener('pointerdown', cancel);
            window.removeEventListener('keydown', onKeyDown);
            isSettlingRef.current = false;
            endSettlingRef.current = undefined;
            mountVisibleSections();
        };

        /**
         * The scroll arrived under its own steam. A section above the target can mount or resize
         * while we were settling — `holdReadingPosition` deliberately skips its own correction then
         * (see its `isSettlingRef` guard) so it doesn't fight this scroll, but that leaves the target
         * wherever that growth pushed it. Re-snapping here, once, recomputes the offset from the
         * current layout rather than a tracked delta, so it lands correctly regardless of what moved
         * above it meanwhile.
         */
        const settle = () => {
            stop();
            scrollToNode(container, node, 'auto');
        };

        /** The user took over: release the watch. */
        const cancel = () => stop();

        /** Each scroll event restarts the quiet-spell timer, so settling waits for movement to stop. */
        const onScroll = () => {
            clearTimeout(timers.idle);
            timers.idle = setTimeout(settle, SETTLE_IDLE_MS);
        };

        const onKeyDown = (event: Event) => {
            if (SCROLLING_KEYS.includes((event as KeyboardEvent).key)) {
                cancel();
            }
        };

        isSettlingRef.current = true;
        endSettlingRef.current = stop;

        container.eventTarget.addEventListener('scroll', onScroll, { passive: true });
        container.eventTarget.addEventListener('scrollend', settle);
        container.eventTarget.addEventListener('wheel', cancel, { passive: true });
        container.eventTarget.addEventListener('touchstart', cancel, { passive: true });
        container.eventTarget.addEventListener('pointerdown', cancel);
        window.addEventListener('keydown', onKeyDown);

        timers.idle = setTimeout(settle, SETTLE_IDLE_MS);
        timers.cap = setTimeout(settle, SETTLE_MAX_MS);

        detachCommandHoldRef.current?.();
        arrivalKeyRef.current = targetKey;

        const detachHold = () => {
            USER_INTENT_EVENTS.forEach((type) => window.removeEventListener(type, releaseHold));
            detachCommandHoldRef.current = undefined;
        };
        const releaseHold = () => {
            detachHold();

            if (arrivalKeyRef.current === targetKey) {
                arrivalKeyRef.current = undefined;
                mountVisibleSections();
            }
        };

        USER_INTENT_EVENTS.forEach((type) => window.addEventListener(type, releaseHold, { passive: true }));
        detachCommandHoldRef.current = detachHold;

        mount([targetKey]);
        scrollToNode(container, node, 'auto');
        // The jump can leave a neighbour on screen without changing the mounted set at all, when the
        // target was already mounted, so the visibility pass cannot be left to MOUNT COMPENSATION.
        mountVisibleSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request]);

    /**
     * LAZY MOUNT — rendering a section only once the user can see it.
     *
     * One rule decides every mount that is not an explicit target: `LAZY_MOUNT_VISIBLE_RATIO` of the
     * section is on screen (`mountVisibleSections`). There is no look-ahead margin and no idea of a
     * "near" section — a margin is what made Trade-In appear fully loaded while still off screen, and
     * no amount of tuning makes a margin agree with what the user sees.
     *
     * The `IntersectionObserver` here is only a trigger: rooted at the resolved scroll container with
     * no margin, it fires as an anchor enters, crosses the ratio, and fills the view, and the rule is
     * then re-applied from live layout. It is not the only trigger — the spy re-applies the rule on
     * every scroll frame, MOUNT COMPENSATION on every change to the mounted set, and the
     * `ResizeObserver` when content grows — so a threshold the observer cannot express (a section
     * taller than the viewport never covers that much of its own area) still mounts. Sections passed
     * while a command settles wait for it; `stop` re-applies the rule once the scroll ends. A browser
     * without `IntersectionObserver` mounts everything. Until a section mounts, its slot renders the
     * skeleton, and because the mounted set only grows it never goes back to one.
     */
    useEffect(() => {
        if (!sectionKeys.length) {
            return undefined;
        }

        if (typeof IntersectionObserver === 'undefined') {
            mount(sectionKeys);
            return undefined;
        }

        let container = createScrollContainer(stackRef.current);

        const onIntersect = () => {
            if (isSettlingRef.current) {
                return;
            }

            mountVisibleSections();
        };

        const connect = () => {
            const created = new IntersectionObserver(onIntersect, { root: container.root, threshold: LAZY_MOUNT_THRESHOLDS });
            nodesRef.current.forEach((node) => created.observe(node));

            return created;
        };

        let observer = connect();

        const onWindowResize = () => {
            const next = createScrollContainer(stackRef.current);

            if (next.root === container.root) {
                return;
            }

            observer.disconnect();
            container = next;
            observer = connect();
        };

        window.addEventListener('resize', onWindowResize);

        return () => {
            window.removeEventListener('resize', onWindowResize);
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, mount, mountVisibleSections]);

    /**
     * SPY — reporting where the user is.
     *
     * The browser never tells you which section is on screen, so this watches the container and works
     * it out: read every section's top in one pass, hand them to the pure `resolveActiveSection`, and
     * report the answer. Nothing else can answer this — free scrolling changes no route and no URL.
     *
     * Reads are throttled to one animation frame and never write layout, so a scroll gesture costs one
     * measurement pass per frame. The container is re-resolved on window `resize`, because the element
     * that scrolls changes with the breakpoint (an inner pane on desktop, the document on tablet).
     *
     * The same `ResizeObserver` holds the reading position. When a section above the active one
     * changes height and no command is settling, the container scrolls by the difference in the same
     * callback, so content mounting above the user does not push them down. Browsers without CSS
     * scroll anchoring need this; the stack opts out of native anchoring so the two never stack up.
     * Each mounted section's height is also recorded, so a later visit reserves the right space.
     */
    useEffect(() => {
        if (!sectionKeys.length) {
            return undefined;
        }

        let container = createScrollContainer(stackRef.current);
        let frame = 0;

        /** One measurement pass: where is everything, and which section does that make active? */
        const observe = () => {
            frame = 0;

            if (isSettlingRef.current) {
                return;
            }

            const sections = sectionKeys
                .map((key) => ({ key, node: nodesRef.current.get(key) }))
                .filter((entry): entry is { key: ShowroomSectionKey; node: HTMLElement } => !!entry.node)
                .map(({ key, node }) => ({ key, top: node.getBoundingClientRect().top }));

            const viewport = container.getViewport();
            const metrics = container.getMetrics();

            const active = resolveActiveSection({
                sections,
                viewportTop: viewport.top,
                viewportHeight: viewport.height,
                ...metrics
            });

            // Compared against the store rather than the last value we reported: the sidebar may have
            // moved the highlight in between, and scrolling back to a section must light it again.
            if (active && active !== getActiveSectionKey(store.getState())) {
                dispatch(setActiveSection(active));
            }

            // The same frame answers the mount question too: the observer's thresholds cannot express
            // a share of the viewport for a section taller than it, and this pass can.
            mountVisibleSections();
        };

        /** Collapses a burst of scroll events into a single read per frame. */
        const schedule = () => {
            if (!frame) {
                frame = requestAnimationFrame(observe);
            }
        };

        const listen = () => container.eventTarget.addEventListener('scroll', schedule, { passive: true });
        const unlisten = () => container.eventTarget.removeEventListener('scroll', schedule);

        listen();

        const compensate = (entries: ResizeObserverEntry[]) => {
            const measurements: Array<readonly [ShowroomSectionKey, number]> = [];

            entries.forEach((entry) => {
                const key = sectionKeyOf(entry.target);

                if (key) {
                    measurements.push([key, entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height]);
                }
            });

            holdReadingPosition(measurements);
            mountVisibleSections();
        };

        // A section growing or shrinking moves every section below it, so it needs a fresh read too.
        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? undefined
                : new ResizeObserver((entries) => {
                      compensate(entries);
                      schedule();
                  });
        nodesRef.current.forEach((node) => resizeObserver?.observe(node));

        /** A breakpoint change can hand the scrolling over to a different element entirely. */
        const onWindowResize = () => {
            unlisten();
            container = createScrollContainer(stackRef.current);
            listen();
            schedule();
        };

        window.addEventListener('resize', onWindowResize);

        return () => {
            unlisten();
            window.removeEventListener('resize', onWindowResize);
            resizeObserver?.disconnect();

            if (frame) {
                cancelAnimationFrame(frame);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, store, signature, stackRef, holdReadingPosition, mountVisibleSections]);

    const isContentMounted = useCallback((key: ShowroomSectionKey) => mountedKeys.has(key), [mountedKeys]);

    return { stackRef, getSectionRef, isContentMounted };
};
